/**
 * FileDownloadService - Download files from remote (clipboard or file manager)
 *
 * When the remote host copies files (Ctrl+C), this service:
 * 1. Receives notification via eventBus (from data channel)
 * 2. Allows user to download files to browser
 *
 * Uses file-ctrl/file-data DataChannels for actual download
 */

import { eventBus } from './EventBus';
import { fileTransferService } from './FileTransferService';

// Debug logging
const DEBUG = true;
const log = DEBUG ? console.log.bind(console, '[ClipboardDownload]') : () => {};
const logError = console.error.bind(console, '[ClipboardDownload]');

export interface ClipboardFileInfo {
  index: number;
  name: string;
  path: string;
  size: number;
  fileCount: number;  // For directories: number of files inside; for files: 1
  isDir: boolean;
}

export interface ClipboardNotification {
  files: ClipboardFileInfo[];
  totalSize: number;
  timestamp: number;
}

export interface DownloadProgress {
  fileIndex: number;
  fileName: string;
  totalSize: number;
  downloadedBytes: number;
  status: 'pending' | 'preparing' | 'downloading' | 'completed' | 'failed';
  // ZIP 下载场景：host 端上报的"已读取原始数据字节" + "原始总字节"
  // 用于精确百分比，弥补 zip 压缩后字节流大小不可预知问题
  readBytes?: number;
  originalTotal?: number;
  // 当前下载速度（B/s），由 FileDownloadService 滑动窗口算出
  bytesPerSecond?: number;
}

class FileDownloadService {
  private ctrlChannel: RTCDataChannel | null = null;
  private dataChannel: RTCDataChannel | null = null;

  // Current notification
  private currentNotification: ClipboardNotification | null = null;

  // Download state
  private downloadBuffer: Uint8Array[] = [];
  private downloadProgress: DownloadProgress | null = null;
  private receivedSize: number = 0;
  private downloadFileName: string = '';
  private isZipDownload: boolean = false;
  private isCancelled: boolean = false;
  private lastProgressUpdate: number = 0; // Throttle progress updates

  // 速度滑动窗口：保留最近 1.5s 的 (timestamp, bytes) 样本
  // ZIP 场景 bytes 用 readBytes（原始数据进度，更直观），单文件用 receivedSize
  private speedSamples: Array<{ ts: number; bytes: number }> = [];
  private static readonly SPEED_WINDOW_MS = 1500;

  constructor() {
    // Listen for file available notifications（拉模式：InputService Ctrl+C / StatusBar 手动拉取触发）
    eventBus.on('clipboard:filesAvailable', (data: {
      files: ClipboardFileInfo[];
      totalSize: number;
      fileCount: number;
    }) => {
      log('Files available:', data.fileCount, 'files');
      this.currentNotification = {
        files: data.files,
        totalSize: data.totalSize,
        timestamp: Date.now(),
      };
    });

    // Listen for channel ready event from FileTransferService
    eventBus.on('fileTransfer:channelsReady', (channels: { ctrl: RTCDataChannel; data: RTCDataChannel }) => {
      log('Channels ready');
      this.setChannels(channels.ctrl, channels.data);
    });
  }

  /**
   * Set data channel references
   */
  private setChannels(ctrl: RTCDataChannel, data: RTCDataChannel): void {
    // Prevent duplicate listeners - check if already set to same channels
    if (this.ctrlChannel === ctrl && this.dataChannel === data) {
      log('Channels already set, skipping');
      return;
    }

    this.ctrlChannel = ctrl;
    this.dataChannel = data;

    // Listen for control messages (calculated, complete, error)
    ctrl.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'clipboard_download') {
          if (msg.action === 'calculated') {
            // Calculated file sizes response — 拉模式下仅供内部 currentNotification 使用
            log('Received calculated:', msg);
            if (msg.files) {
              this.currentNotification = {
                files: msg.files,
                totalSize: msg.totalSize || 0,
                timestamp: Date.now(),
              };
            }
          } else if (msg.action === 'progress') {
            // ZIP 流式发送中 host 上报的原始字节进度
            if (this.downloadProgress && this.isZipDownload) {
              const readBytes = typeof msg.readBytes === 'number' ? msg.readBytes : 0;
              const totalBytes = typeof msg.totalBytes === 'number' ? msg.totalBytes : 0;
              this.downloadProgress.readBytes = readBytes;
              this.downloadProgress.originalTotal = totalBytes;
              // status 进入 downloading 阶段（preparing 结束）
              if (this.downloadProgress.status === 'preparing') {
                this.downloadProgress.status = 'downloading';
              }
              // 速度：用 readBytes 作样本（与百分比口径一致）
              const speed = this.pushSpeedSampleAndCompute(readBytes);
              this.downloadProgress.bytesPerSecond = speed;
              eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });
            }
          } else if (msg.action === 'complete') {
            this.handleComplete(msg);
          } else if (msg.action === 'error') {
            this.handleError(msg);
          }
        }
      } catch {
        // Ignore
      }
    });

    // Listen for binary data
    data.addEventListener('message', (event) => {
      if (this.isCancelled) return; // Ignore data if cancelled
      if (!this.downloadProgress || (this.downloadProgress.status !== 'downloading' && this.downloadProgress.status !== 'preparing')) {
        return;
      }
      if (this.downloadProgress.status === 'preparing') {
        this.downloadProgress.status = 'downloading';
      }
      if (event.data instanceof ArrayBuffer || event.data?.byteLength !== undefined) {
        const chunk = new Uint8Array(event.data);
        this.downloadBuffer.push(chunk);
        this.receivedSize += chunk.length;
        this.downloadProgress.downloadedBytes = this.receivedSize;

        // 单文件场景：用 receivedSize 算速度（=原始字节，1:1，与百分比口径一致）
        // ZIP 场景：速度由 host 的 progress 消息驱动，这里跳过避免双源
        if (!this.isZipDownload) {
          const speed = this.pushSpeedSampleAndCompute(this.receivedSize);
          this.downloadProgress.bytesPerSecond = speed;
        }

        // Throttle progress updates to avoid UI freeze (max 10 updates/second)
        const now = Date.now();
        if (now - this.lastProgressUpdate > 100) {
          this.lastProgressUpdate = now;
          eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });
        }
      }
    });

    log('Channels set');
  }

  /**
   * 追加速度样本并返回估算速度（B/s）。
   * bytes 取值：ZIP 场景下用 host 上报的 readBytes（原始数据进度，与百分比口径一致），
   * 单文件下载用 receivedSize（=原始字节，1:1）。
   */
  private pushSpeedSampleAndCompute(bytes: number): number {
    const now = Date.now();
    this.speedSamples.push({ ts: now, bytes });
    // 修剪：丢弃 1.5s 前的样本
    const cutoff = now - FileDownloadService.SPEED_WINDOW_MS;
    while (this.speedSamples.length > 0 && this.speedSamples[0].ts < cutoff) {
      this.speedSamples.shift();
    }
    if (this.speedSamples.length < 2) return 0;
    const first = this.speedSamples[0];
    const last = this.speedSamples[this.speedSamples.length - 1];
    const dt = last.ts - first.ts;
    if (dt <= 0) return 0;
    return ((last.bytes - first.bytes) * 1000) / dt;
  }

  private resetSpeedSamples(): void {
    this.speedSamples = [];
  }

  /**
   * Cancel current download
   */
  cancelDownload(): void {
    log('Cancelling download');
    this.isCancelled = true;
    this.downloadBuffer = [];
    this.receivedSize = 0;
    this.resetSpeedSamples();
    if (this.downloadProgress) {
      this.downloadProgress.status = 'failed';
      eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });
    }
    this.downloadProgress = null;

    // Notify Go to stop sending
    if (this.ctrlChannel && this.ctrlChannel.readyState === 'open') {
      const msg = {
        type: 'clipboard_download',
        action: 'cancel',
      };
      this.sendChunkedMessage(msg);
    }
  }

  /**
   * Send message in chunked format expected by Go
   */
  private sendChunkedMessage(msg: object): void {
    log('sendChunkedMessage: ctrlChannel=', this.ctrlChannel ? 'exists' : 'null',
        'readyState=', this.ctrlChannel?.readyState);
    if (!this.ctrlChannel || this.ctrlChannel.readyState !== 'open') {
      logError('Control channel not ready, state:', this.ctrlChannel?.readyState);
      return;
    }
    const data = JSON.stringify(msg);
    // Wrap in chunk format: {c: chunkIndex, t: totalChunks, d: data}
    const chunk = { c: 0, t: 1, d: data };
    this.ctrlChannel.send(JSON.stringify(chunk));
    log('Message sent successfully');
  }

  /**
   * Request calculation of file sizes
   */
  async requestCalculate(): Promise<void> {
    log('Requesting calculate...');

    // Ensure channels are ready
    const ready = await this.ensureChannels();
    if (!ready || !this.ctrlChannel) {
      logError('Cannot request calculate: channels not ready');
      return;
    }

    const msg = {
      type: 'clipboard_download',
      action: 'calculate',
    };

    log('Sending calculate request');
    this.sendChunkedMessage(msg);
  }

  /**
   * Ensure channels are ready for download
   * FileTransferService creates channels on-demand
   */
  private async ensureChannels(): Promise<boolean> {
    log('ensureChannels: checking current channels...');
    log('  ctrlChannel:', this.ctrlChannel ? `exists, state=${this.ctrlChannel.readyState}` : 'null');
    log('  dataChannel:', this.dataChannel ? `exists, state=${this.dataChannel.readyState}` : 'null');

    if (this.ctrlChannel?.readyState === 'open' && this.dataChannel?.readyState === 'open') {
      log('ensureChannels: channels already open');
      return true;
    }

    // Ask FileTransferService to create/ensure channels
    log('ensureChannels: requesting FileTransferService to create channels...');
    const ready = await fileTransferService.ensureChannelsAsync();
    if (!ready) {
      log('ensureChannels: FileTransferService failed to create channels');
      return false;
    }

    // Get channels from FileTransferService
    const ctrl = fileTransferService.getCtrlChannel();
    const data = fileTransferService.getDataChannel();

    if (ctrl?.readyState === 'open' && data?.readyState === 'open') {
      log('ensureChannels: got channels from FileTransferService');
      this.setChannels(ctrl, data);
      return true;
    }

    log('ensureChannels: channels not ready after ensureChannelsAsync');
    return false;
  }

  /**
   * Download a single file
   */
  async downloadFile(fileIndex: number): Promise<void> {
    if (!this.currentNotification) {
      throw new Error('没有可下载的文件');
    }

    const file = this.currentNotification.files.find(f => f.index === fileIndex);
    if (!file || file.isDir) {
      throw new Error('无效的文件');
    }

    // Ensure channels are ready
    const ready = await this.ensureChannels();
    if (!ready || !this.ctrlChannel) {
      throw new Error('无法建立下载通道');
    }

    // Initialize download state
    this.downloadBuffer = [];
    this.receivedSize = 0;
    this.downloadFileName = file.name;
    this.isZipDownload = false;

    this.downloadProgress = {
      fileIndex,
      fileName: file.name,
      totalSize: file.size,
      downloadedBytes: 0,
      status: 'downloading',
    };

    eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });

    // Send download request with file path (Go reads file directly)
    const msg = {
      type: 'clipboard_download',
      action: 'download',
      filePath: file.path,
      fileName: file.name,
    };

    log('Sending download request:', msg);
    this.sendChunkedMessage(msg);
  }

  /**
   * Download all files - single file direct, multiple files as ZIP
   */
  async downloadAll(): Promise<void> {
    if (!this.currentNotification) {
      throw new Error('没有可下载的文件');
    }

    const files = this.currentNotification.files;
    const totalFiles = files.reduce((sum, f) => sum + (f.fileCount || 1), 0);
    if (totalFiles === 0) {
      throw new Error('没有可下载的文件');
    }

    // Ensure channels are ready
    const ready = await this.ensureChannels();
    if (!ready || !this.ctrlChannel) {
      throw new Error('无法建立下载通道');
    }

    // Single non-directory file: download directly
    if (files.length === 1 && !files[0].isDir) {
      log('Single file, downloading directly');
      return this.downloadFile(files[0].index);
    }

    // Multiple files or directory: download as ZIP
    this.downloadBuffer = [];
    this.receivedSize = 0;
    this.isZipDownload = true;

    // Use directory name or first file name as ZIP name
    let zipName = 'clipboard_files';
    if (files.length === 1 && files[0].isDir) {
      // Single directory: use directory name
      zipName = files[0].name;
    } else if (files.length > 0) {
      // Multiple files: use parent directory name from path
      const path = files[0].path;
      const parts = path.replace(/\\/g, '/').split('/');
      if (parts.length >= 2) {
        zipName = parts[parts.length - 2] || 'clipboard_files';
      }
    }
    this.downloadFileName = `${zipName}.zip`;

    this.downloadProgress = {
      fileIndex: -1,
      fileName: this.downloadFileName,
      totalSize: this.currentNotification.totalSize,
      downloadedBytes: 0,
      status: 'preparing',
    };

    eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });

    // Send download all request with file list (Go reads files directly)
    const msg = {
      type: 'clipboard_download',
      action: 'download_all',
      fileName: this.downloadFileName,
      files: files.map(f => ({
        name: f.name,
        path: f.path,
        size: f.size,
        isDir: f.isDir,
      })),
    };

    log('Sending download all request:', msg);
    this.sendChunkedMessage(msg);
  }

  /**
   * Download with specific files list and filename
   */
  async downloadWithFiles(files: Array<{name: string; path: string; size: number; isDir: boolean}>, fileName: string): Promise<void> {
    if (files.length === 0) {
      throw new Error('没有可下载的文件');
    }

    // Ensure channels are ready
    const ready = await this.ensureChannels();
    if (!ready || !this.ctrlChannel) {
      throw new Error('无法建立下载通道');
    }

    // Initialize download state
    this.isCancelled = false;
    this.downloadBuffer = [];
    this.receivedSize = 0;
    this.downloadFileName = fileName;
    this.lastProgressUpdate = 0;
    this.resetSpeedSamples();

    // Single non-directory file: download directly
    if (files.length === 1 && !files[0].isDir) {
      this.isZipDownload = false;
      this.downloadProgress = {
        fileIndex: 0,
        fileName: files[0].name,
        totalSize: files[0].size,
        downloadedBytes: 0,
        status: 'downloading',
      };
      eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });

      const msg = {
        type: 'clipboard_download',
        action: 'download',
        filePath: files[0].path,
        fileName: files[0].name,
      };
      log('Sending download request:', msg);
      this.sendChunkedMessage(msg);
      return;
    }

    // Multiple files or directory: download as ZIP
    this.isZipDownload = true;
    const hasUnknownSize = files.some(f => f.isDir || f.size <= 0);
    const totalSize = hasUnknownSize ? 0 : files.reduce((sum, f) => sum + (f.size > 0 ? f.size : 0), 0);

    this.downloadProgress = {
      fileIndex: -1,
      fileName: fileName,
      totalSize: totalSize,
      downloadedBytes: 0,
      status: 'preparing',
    };
    eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });

    const msg = {
      type: 'clipboard_download',
      action: 'download_all',
      fileName: fileName,
      files: files.map(f => ({
        name: f.name,
        path: f.path,
        size: f.size,
        isDir: f.isDir,
      })),
    };

    log('Sending download with files:', msg);
    this.sendChunkedMessage(msg);
  }

  /**
   * Handle download complete
   */
  private handleComplete(msg: { fileIndex?: number; totalSize?: number }): void {
    if (this.isCancelled) {
      log('Download was cancelled, ignoring complete');
      return;
    }
    if (!this.downloadProgress) return;

    const expectedSize = msg.totalSize || 0;
    log('Download complete signal received, expected size:', expectedSize);

    // Wait for all data to arrive (ctrl channel may arrive before data channel)
    const checkAndFinish = () => {
      if (this.isCancelled) {
        log('Download cancelled while waiting for data');
        return;
      }

      const totalLength = this.downloadBuffer.reduce((sum, arr) => sum + arr.length, 0);

      if (expectedSize > 0 && totalLength < expectedSize) {
        // Still waiting for data
        log('Waiting for data:', totalLength, '/', expectedSize);
        setTimeout(checkAndFinish, 50);
        return;
      }

      log('All data received:', totalLength, 'bytes');
      log('isZipDownload:', this.isZipDownload, 'downloadFileName:', this.downloadFileName);

      // Merge all chunks
      const merged = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of this.downloadBuffer) {
        merged.set(chunk, offset);
        offset += chunk.length;
      }
      log('Merged buffer size:', merged.length);

      // Create blob and trigger download
      const mimeType = this.isZipDownload ? 'application/zip' : 'application/octet-stream';
      const blob = new Blob([merged], { type: mimeType });
      const url = URL.createObjectURL(blob);
      log('Blob created, size:', blob.size, 'url:', url);

      const a = document.createElement('a');
      a.href = url;
      a.download = this.downloadFileName;
      document.body.appendChild(a);
      log('Triggering download click...');
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      log('Download triggered');

      // Update status
      if (this.downloadProgress) {
        this.downloadProgress.status = 'completed';
        this.downloadProgress.downloadedBytes = totalLength;
        eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });
      }

      // Show toast
      eventBus.emit('ui:toast', {
        message: `下载完成: ${this.downloadFileName}`,
        type: 'success',
      });

      // Cleanup
      this.downloadBuffer = [];
      this.downloadProgress = null;
    };

    checkAndFinish();
  }

  /**
   * Handle error
   */
  private handleError(msg: { error?: string }): void {
    logError('下载错误:', msg.error);

    if (this.downloadProgress) {
      this.downloadProgress.status = 'failed';
      eventBus.emit('clipboard:downloadProgress', { ...this.downloadProgress });
    }

    eventBus.emit('ui:toast', {
      message: `下载失败: ${msg.error}`,
      type: 'error',
    });

    // Cleanup
    this.downloadBuffer = [];
    this.downloadProgress = null;
  }

  /**
   * Get current notification
   */
  getNotification(): ClipboardNotification | null {
    return this.currentNotification;
  }

  /**
   * Check if download is in progress
   */
  isDownloading(): boolean {
    return this.downloadProgress?.status === 'downloading';
  }
}

export const fileDownloadService = new FileDownloadService();
export default fileDownloadService;
